import { SpaceAssignment, SpaceRelation, SpaceType } from './SpaceAssignments';

/**
 * Pure helper extracted from `SpaceAssignmentTable.handleUpdateAccess`.
 *
 * Encapsulates the assign-then-remove ordering, the same-value short
 * circuit, and the error handling, so the logic can be unit-tested
 * without mounting the full table + Antd Select. The component supplies
 * three callbacks: `assign`, `remove`, and the side-effect emitters.
 *
 * Order matters: ASSIGN the new relation first, then REMOVE the old one.
 * Both writes are idempotent in OpenFGA, so the user briefly holding both
 * relations is harmless, and a failure on either step never leaves them
 * with no relation. This is the regression we keep paying for if the
 * order ever flips back.
 */
export interface UpdateRelationParams {
  userId: string;
  spaceType: SpaceType;
  spaceId: string;
  oldRelation: SpaceRelation;
  newRelation: SpaceRelation;
  assign: (input: AssignInput) => Promise<unknown>;
  remove: (input: AssignInput) => Promise<unknown>;
  onSuccess: () => void;
  onError: (error: unknown) => void;
}

export interface AssignInput {
  user_id: string;
  space_type: SpaceType;
  space_id: string;
  relation: SpaceRelation;
}

export type UpdateRelationOutcome = 'noop' | 'updated' | 'failed';

export async function updateSpaceRelation(
  params: UpdateRelationParams,
): Promise<UpdateRelationOutcome> {
  const {
    userId,
    spaceType,
    spaceId,
    oldRelation,
    newRelation,
    assign,
    remove,
    onSuccess,
    onError,
  } = params;

  if (newRelation === oldRelation) {
    return 'noop';
  }

  try {
    await assign({
      user_id: userId,
      space_type: spaceType,
      space_id: spaceId,
      relation: newRelation,
    });
    await remove({
      user_id: userId,
      space_type: spaceType,
      space_id: spaceId,
      relation: oldRelation,
    });
    onSuccess();
    return 'updated';
  } catch (error) {
    onError(error);
    return 'failed';
  }
}

// Convenience for callers that have a `SpaceAssignment` record on hand.
export function updateSpaceRelationFromRecord(
  record: Pick<SpaceAssignment, 'space_type' | 'space_id' | 'relation'>,
  rest: Omit<UpdateRelationParams, 'spaceType' | 'spaceId' | 'oldRelation' | 'newRelation'> & {
    newRelation: SpaceRelation;
  },
): Promise<UpdateRelationOutcome> {
  return updateSpaceRelation({
    spaceType: record.space_type,
    spaceId: record.space_id,
    oldRelation: record.relation,
    ...rest,
  });
}
